import { useEffect, useState } from 'react';
import I18n from 'I18n';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { TFlex } from 'foundations-components/transitional/components/TFlex';
import { useUpgradeModal } from 'ui-addon-upgrades/ums/useUpgradeModal';
import { DATASET_INSIGHT_TYPES } from 'reporting-data/tsTypes/datasetInsightTypes';
import { EVENTS } from 'reporting-ui-components/lib/events';
import useCloneTracker from 'reporting-ui-components/lib/useCloneTracker';
import { FormControl } from 'foundations-components/transitional/components/FormControl';
import { InlineLabel } from 'foundations-components/transitional/components/InlineLabel';
import { Select } from 'foundations-components/transitional/components/Select';
import { isFailed, isLoading, isSucceeded } from 'reporting-data/asyncData/AsyncData';
import { ADVANCED_INSIGHTS_UPGRADE_KEY, ADVANCED_INSIGHT_TYPES } from '../../constants/datasetInsights';
import { ADVANCED_REPORTING_INSIGHTS_SCOPE } from '../../constants/scopes';
import useFetchDatasetInsightTypeOptions from '../../hooks/useFetchDatasetInsightTypeOptions';
import useFetchUserInfo from '../../hooks/useFetchUserInfo';
import { buildDatasetInsightOptions, disableDatasetInsights, enableDatasetInsightType, getSelectedAnalyticsTrendLineOptionType } from '../../lib/datasetInsightUtils';
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
const onChangeToggle = args => {
  const updatedReport = args.newValue ? enableDatasetInsightType(args.report, args.newValue) : args.previousValue ? disableDatasetInsights(args.report, args.previousValue) : args.report;
  args.onChange(updatedReport);
};
export const TrendLineToggle = ({
  appUsageTracker,
  trackingAssetType,
  trackingApplicationName,
  trackingScreenName,
  report,
  onChange,
  buttonUse = 'transparent',
  onFailureToLoad
}) => {
  const {
    openUpgradeModal
  } = useUpgradeModal(ADVANCED_INSIGHTS_UPGRADE_KEY);
  const [isAlreadyDisplayed, setIsAlreadyDisplayed] = useState(false);
  const usageTracker = useCloneTracker(appUsageTracker);
  const userInfo = useFetchUserInfo();
  const gates = (userInfo === null || userInfo === void 0 ? void 0 : userInfo.gates) || [];
  const hasAdvancedInsightsAccess = (userInfo === null || userInfo === void 0 ? void 0 : userInfo.user.scopes.includes(ADVANCED_REPORTING_INSIGHTS_SCOPE)) || false;
  const fetchedInsightOptions = useFetchDatasetInsightTypeOptions(report);
  const LabelComponent = buttonUse === 'transparent' ? InlineLabel : FormControl;
  const validDatasetInsightTypeOptions = isSucceeded(fetchedInsightOptions) ? buildDatasetInsightOptions(fetchedInsightOptions.data, hasAdvancedInsightsAccess) : [];
  const datasetInsightTypeOptions = validDatasetInsightTypeOptions.filter(option => option.value !== DATASET_INSIGHT_TYPES.ANOMALY_DETECTION);
  const cannotEnableTrendLine = isFailed(fetchedInsightOptions) || isSucceeded(fetchedInsightOptions) && datasetInsightTypeOptions.length === 0;
  useEffect(() => {
    if (!isLoading(fetchedInsightOptions)) {
      if (validDatasetInsightTypeOptions.length === 0) {
        setIsAlreadyDisplayed(false);
      } else {
        setIsAlreadyDisplayed(true);
      }
    }
  }, [fetchedInsightOptions, isAlreadyDisplayed, validDatasetInsightTypeOptions.length]);
  useEffect(() => {
    if (cannotEnableTrendLine) {
      onFailureToLoad === null || onFailureToLoad === void 0 || onFailureToLoad();
    }
  }, [cannotEnableTrendLine, onFailureToLoad]);
  if (cannotEnableTrendLine || isLoading(fetchedInsightOptions) && !isAlreadyDisplayed) {
    return /*#__PURE__*/_jsx(_Fragment, {});
  }
  const selectedDatasetInsightType = getSelectedAnalyticsTrendLineOptionType(report);
  const selectedDatasetInsightTypeIsSupported = datasetInsightTypeOptions.some(option => option.value === selectedDatasetInsightType);
  return /*#__PURE__*/_jsx("div", {
    "data-test-id": "trend-line-toggle-container",
    children: /*#__PURE__*/_jsx(LabelComponent, {
      "data-test-id": "trend-line-toggle-label-container",
      label: /*#__PURE__*/_jsx(TFlex, {
        align: "center",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "report-customization-components.customization-components.trendLine.toggleLabel"
        })
      }),
      children: /*#__PURE__*/_jsx(Select, {
        disabled: isLoading(fetchedInsightOptions) && isAlreadyDisplayed,
        padding: buttonUse === 'transparent' ? '0' : undefined,
        "data-test-id": "trend-line-toggle-element",
        buttonUse: buttonUse,
        menuWidth: buttonUse === 'transparent' ? 250 : undefined,
        options: datasetInsightTypeOptions,
        clearable: true,
        placeholder: I18n.text('report-customization-components.customization-components.trendLine.placeholder'),
        value: selectedDatasetInsightTypeIsSupported ? selectedDatasetInsightType : null,
        onChange: ({
          target: {
            value
          }
        }) => {
          if (value in ADVANCED_INSIGHT_TYPES && !hasAdvancedInsightsAccess) {
            openUpgradeModal();
            return;
          }
          const trackerProps = value ?
          // when enabling, set the value to what has been selected
          {
            action: 'enable',
            value
          } :
          // when disabling, set the value to what was selected before disabling
          {
            action: 'disable',
            value: selectedDatasetInsightType
          };
          usageTracker.track(EVENTS.toggleDatasetInsights, Object.assign({}, trackerProps, {
            assetType: trackingAssetType || 'report',
            applicationName: trackingApplicationName,
            whereScreen: trackingScreenName
          }));
          onChangeToggle({
            previousValue: selectedDatasetInsightType,
            // UISelect has a finite list of supported types (i.e. string, number, etc), as opposed to a generic
            // where we could indicate that the values are DatasetInsighttypes | undefined
            //
            // Coercing so that the onChangeToggle function can have appropriately typed parameters
            newValue: value,
            report,
            onChange,
            gates
          });
        }
      })
    })
  });
};
export const __TESTABLE__ = {
  onChangeToggle
};